/**
 * Hooks de datos. Un hook por endpoint de la API, con la forma del payload
 * declarada acá: el front no redefine los tipos de dominio (vienen de
 * `@ops/contracts`), solo el **sobre** con el que cada ruta los envuelve.
 *
 * Ningún hook filtra campos por rol. El recorte de $$ pasa en el servidor: si
 * el front fuera la autoridad, bastaría abrir las devtools para ver el MRR.
 */
import type {
  AcquisitionPayload,
  ActivationRow,
  Agente,
  AppConfig,
  Channel,
  Client,
  Comment,
  CreateCommentInput,
  CreateDatapointInput,
  CreateFollowupInput,
  DataSource,
  EntityType,
  Followup,
  FollowupsByOwner,
  FunnelDatapoint,
  FunnelItem,
  FunnelStage,
  FunnelSummary,
  Interview,
  InterviewBlockDef,
  InterviewsPayload,
  MetricDefinition,
  MetricValue,
  OverviewPayload,
  PortfolioHealth,
  RevenueMonth,
  RevenuePayload,
  SaveInterviewAnswersInput,
  SeoQuery,
  UnitEconomics,
  UpsertRevenueMonthInput,
  UsagePayload,
  User,
  UserRef,
  Links,
} from '@ops/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiPost, apiPut } from './api'

/** Todo payload que lista entidades externas viaja con su sobre de deep links. */
interface ConLinks {
  links: Links
}

export type OverviewResponse = OverviewPayload & ConLinks

export interface ClientsResponse extends ConLinks {
  periodo: string
  clientes: Client[]
  portfolio: PortfolioHealth
}

export interface ClientDetailResponse extends ConLinks {
  periodo: string
  cliente: Client
  agentes: Agente[]
  comentarios: Comment[]
  followups: Followup[]
}

export interface ActivationTotales {
  contratados: number
  instalados: number
  brecha: number
  pctActivado: number
  /** Cuentas sin medición: la brecha real puede ser mayor que la que se ve. */
  sinMedicion: number
}

export interface ActivationResponse extends ConLinks {
  periodo: string
  filas: ActivationRow[]
  totales: ActivationTotales
}

export type RevenueResponse = RevenuePayload & ConLinks

/**
 * `/usage` **no** se extiende con `ConLinks`: el sobre de links ya está dentro
 * del payload del contrato (`usageBaseShape.links`), y volver a intersectarlo
 * rompería la unión discriminada por rol — TypeScript perdería el estrechamiento
 * por `rol` y `economiaPorCuenta` pasaría a ser accesible en las tres ramas, que
 * es exactamente el corte que este módulo tiene que respetar.
 */
export type UsageResponse = UsagePayload

export type AcquisitionResponse = AcquisitionPayload &
  ConLinks & { resumen: FunnelSummary }

export interface FunnelResponse extends ConLinks {
  periodo: string
  etapas: FunnelStage[]
  datapoints: FunnelDatapoint[]
  oportunidades: FunnelItem[]
  resumen: FunnelSummary
}

export interface SeoResponse extends ConLinks {
  periodo: string
  queries: SeoQuery[]
}

export interface MetricsResponse {
  periodo: string
  metricas: MetricDefinition[]
  valores: MetricValue[]
}

export type InterviewsResponse = InterviewsPayload & {
  /** El cuestionario viaja con el payload: es contenido versionado del contrato. */
  cuestionario: readonly InterviewBlockDef[]
  totalPreguntas: number
}

export interface FollowupsResponse extends ConLinks {
  grupos: FollowupsByOwner
}

export interface CreateFollowupResponse extends ConLinks {
  followup: Followup
}

// ---------------------------------------------------------------------------
// Claves. Centralizadas para que invalidar no dependa de recordar un string.
// ---------------------------------------------------------------------------

export const queryKeys = {
  me: ['me'] as const,
  users: ['users'] as const,
  config: ['config'] as const,
  overview: (periodo: string) => ['overview', periodo] as const,
  clients: (periodo: string, filtros?: ClientsFiltros) =>
    ['clients', periodo, filtros ?? {}] as const,
  client: (id: string, periodo: string) => ['client', id, periodo] as const,
  activation: (periodo: string) => ['activation', periodo] as const,
  revenue: (periodo: string) => ['revenue', periodo] as const,
  usage: (periodo: string) => ['usage', periodo] as const,
  unitEconomics: (periodo: string) => ['unit-economics', periodo] as const,
  acquisition: (periodo: string) => ['acquisition', periodo] as const,
  funnel: (periodo: string) => ['acquisition', 'funnel', periodo] as const,
  channels: ['acquisition', 'channels'] as const,
  seo: (periodo: string) => ['acquisition', 'seo', periodo] as const,
  metrics: (periodo: string) => ['metrics', periodo] as const,
  sources: ['sources'] as const,
  interviews: ['interviews'] as const,
  interview: (id: string) => ['interviews', id] as const,
  comments: (entityType: EntityType, entityId: string) =>
    ['comments', entityType, entityId] as const,
  followups: (mine: boolean) => ['followups', { mine }] as const,
}

export interface ClientsFiltros {
  estado?: string
  segmento?: string
  q?: string
  ownerId?: string
}

// ---------------------------------------------------------------------------
// Identidad y configuración
// ---------------------------------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => apiGet<User>('/api/me'),
    staleTime: Infinity,
    retry: false,
  })
}

/** Directorio de personas asignables, para el selector de dueño. */
export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => apiGet<UserRef[]>('/api/users'),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Config que el front no puede inventar: si los datos son dummy (banner) y las
 * bases de URL de los sistemas de origen. Sale del servidor para que el front
 * nunca hardcodee el dominio de Plane, Close o Stripe.
 */
export function useAppConfigQuery() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: () => apiGet<AppConfig>('/api/config'),
    staleTime: Infinity,
  })
}

// ---------------------------------------------------------------------------
// Pantallas
// ---------------------------------------------------------------------------

export function useOverview(periodo: string) {
  return useQuery({
    queryKey: queryKeys.overview(periodo),
    queryFn: () => apiGet<OverviewResponse>('/api/overview', { periodo }),
  })
}

export function useClients(periodo: string, filtros: ClientsFiltros = {}) {
  return useQuery({
    queryKey: queryKeys.clients(periodo, filtros),
    queryFn: () =>
      apiGet<ClientsResponse>('/api/clients', { periodo, ...filtros }),
  })
}

export function useClient(id: string, periodo: string) {
  return useQuery({
    queryKey: queryKeys.client(id, periodo),
    queryFn: () =>
      apiGet<ClientDetailResponse>(`/api/clients/${encodeURIComponent(id)}`, {
        periodo,
      }),
    // La ficha se abre por URL, así que un id inexistente es un caso normal. Un
    // 404 no se arregla reintentando: reintentarlo tres veces solo demora en
    // decir lo que ya se sabe. El 5xx sí se reintenta, que ahí puede ser un
    // hipo de la red.
    retry: (intentos, error) =>
      error instanceof ApiError && error.status < 500 ? false : intentos < 3,
  })
}

export function useActivation(periodo: string) {
  return useQuery({
    queryKey: queryKeys.activation(periodo),
    queryFn: () => apiGet<ActivationResponse>('/api/activation', { periodo }),
  })
}

export function useRevenue(periodo: string) {
  return useQuery({
    queryKey: queryKeys.revenue(periodo),
    queryFn: () => apiGet<RevenueResponse>('/api/revenue', { periodo }),
  })
}

/**
 * Consumo del período. Es el único payload con dólares adentro que piden los
 * tres roles: el costo de servir viaja para todos y el precio y el margen los
 * recorta el servidor (`stripMoney`), así que este hook no filtra nada.
 */
export function useUsage(periodo: string) {
  return useQuery({
    queryKey: queryKeys.usage(periodo),
    queryFn: () => apiGet<UsageResponse>('/api/usage', { periodo }),
  })
}

export function useUnitEconomics(periodo: string) {
  return useQuery({
    queryKey: queryKeys.unitEconomics(periodo),
    queryFn: () => apiGet<UnitEconomics>('/api/unit-economics', { periodo }),
  })
}

export function useAcquisition(periodo: string) {
  return useQuery({
    queryKey: queryKeys.acquisition(periodo),
    queryFn: () => apiGet<AcquisitionResponse>('/api/acquisition', { periodo }),
  })
}

export function useFunnel(periodo: string) {
  return useQuery({
    queryKey: queryKeys.funnel(periodo),
    queryFn: () =>
      apiGet<FunnelResponse>('/api/acquisition/funnel', { periodo }),
  })
}

export function useChannels() {
  return useQuery({
    queryKey: queryKeys.channels,
    queryFn: () =>
      apiGet<{ canales: Channel[] }>('/api/acquisition/channels'),
  })
}

export function useSeoQueries(periodo: string) {
  return useQuery({
    queryKey: queryKeys.seo(periodo),
    queryFn: () => apiGet<SeoResponse>('/api/acquisition/seo', { periodo }),
  })
}

export function useMetrics(periodo: string) {
  return useQuery({
    queryKey: queryKeys.metrics(periodo),
    queryFn: () => apiGet<MetricsResponse>('/api/metrics', { periodo }),
  })
}

export function useDataSources() {
  return useQuery({
    queryKey: queryKeys.sources,
    queryFn: () => apiGet<{ fuentes: DataSource[] }>('/api/sources'),
  })
}

export function useInterviews() {
  return useQuery({
    queryKey: queryKeys.interviews,
    queryFn: () => apiGet<InterviewsResponse>('/api/interviews'),
  })
}

export function useInterview(id: string) {
  return useQuery({
    queryKey: queryKeys.interview(id),
    queryFn: () =>
      apiGet<Interview>(`/api/interviews/${encodeURIComponent(id)}`),
  })
}

export function useComments(entityType: EntityType, entityId: string) {
  return useQuery({
    queryKey: queryKeys.comments(entityType, entityId),
    queryFn: () =>
      apiGet<Comment[]>('/api/comments', { entityType, entityId }),
  })
}

export function useFollowups(mine: boolean) {
  return useQuery({
    queryKey: queryKeys.followups(mine),
    queryFn: () =>
      // `mine` solo viaja cuando es true: la API lo lee como `=== 'true'`.
      apiGet<FollowupsResponse>('/api/followups', {
        mine: mine ? 'true' : undefined,
      }),
  })
}

// ---------------------------------------------------------------------------
// Mutaciones
// ---------------------------------------------------------------------------

export function useCreateComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCommentInput) =>
      apiPost<Comment>('/api/comments', input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.comments(input.entityType, input.entityId),
      })
    },
  })
}

/**
 * Crea el seguimiento en Plane. Si Plane falla, la mutación queda en error y el
 * diálogo muestra "Reintentar": nunca se guarda un seguimiento huérfano que el
 * usuario crea que existe.
 */
export function useCreateFollowup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFollowupInput) =>
      apiPost<CreateFollowupResponse>('/api/followups', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['followups'] })
      void qc.invalidateQueries({ queryKey: ['overview'] })
    },
  })
}

/** Carga manual de un datapoint. Es el reemplazo de editar la celda del sheet. */
export function useCreateDatapoint(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDatapointInput) =>
      apiPost<MetricValue>(
        `/api/metrics/${encodeURIComponent(slug)}/datapoints`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['metrics'] })
      void qc.invalidateQueries({ queryKey: ['overview'] })
    },
  })
}

export function useUpsertRevenueMonth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpsertRevenueMonthInput) =>
      apiPut<RevenueMonth>(
        `/api/revenue/${encodeURIComponent(input.periodo)}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['revenue'] })
      void qc.invalidateQueries({ queryKey: ['overview'] })
    },
  })
}

export function useSaveInterviewAnswers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveInterviewAnswersInput) =>
      apiPut<Interview>(
        `/api/interviews/${encodeURIComponent(input.interviewId)}/answers`,
        input,
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.interviews })
      void qc.invalidateQueries({
        queryKey: queryKeys.interview(input.interviewId),
      })
    },
  })
}
